import { useEffect, useState, useMemo } from "react";
import { createFileRoute, useNavigate, useParams } from "@tanstack/react-router";
import {
  ClipboardList,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Calendar,
  RotateCcw,
  Search,
  X,
} from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { useLanguage } from "@/hooks/use-language";
import { useTestHistory, useCreateTest } from "@/hooks/use-tests";
import { getTestSession } from "@/lib/api";
import { createErrorMessage } from "@/lib/error-message";
import type { TestSession } from "@/lib/types";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Card,
  CardContent,
} from "@/components/ui/card";

export const Route = createFileRoute("/_main/exams/$slug/history")({
  component: HistoryPage,
});

const PER_PAGE = 10;

export function HistoryPage() {
  const params = useParams({ strict: false }) as { slug: string };
  const examSlug = params.slug;
  const navigate = useNavigate();
  const { loading: authLoading } = useAuth();
  const { t, locale } = useLanguage();
  const [page, setPage] = useState(1);
  const [retryingId, setRetryingId] = useState<string | null>(null);
  const [questionNumberInput, setQuestionNumberInput] = useState("");
  const [activeQuestionNumber, setActiveQuestionNumber] = useState<number | undefined>(undefined);

  const {
    data: tests = [],
    isLoading: loading,
    error,
  } = useTestHistory(examSlug, activeQuestionNumber);
  const createTestMutation = useCreateTest(examSlug);

  useEffect(() => {
    setPage(1);
  }, [activeQuestionNumber]);

  const totalPages = Math.max(1, Math.ceil(tests.length / PER_PAGE));
  const paginatedTests = useMemo(() => {
    const start = (page - 1) * PER_PAGE;
    return tests.slice(start, start + PER_PAGE);
  }, [tests, page]);

  if (authLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-zinc-400" />
      </div>
    );
  }

  function formatDate(dateStr: string): string {
    const date = new Date(dateStr);
    return date.toLocaleDateString(locale === "en" ? "en-US" : "ko-KR", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }

  function getAccuracy(test: TestSession): number {
    if (test.total_questions === 0) return 0;
    return Math.round((test.correct_count / test.total_questions) * 100);
  }

  function getScoreBadgeVariant(
    rate: number
  ): "default" | "secondary" | "destructive" {
    if (rate >= 80) return "default";
    if (rate >= 60) return "secondary";
    return "destructive";
  }

  function getStatusLabel(test: TestSession): {
    text: string;
    variant: "default" | "secondary" | "outline";
  } {
    if (test.completed_at) {
      return { text: t("history.completed"), variant: "default" };
    }
    return { text: t("history.continue"), variant: "outline" };
  }

  async function handleRetake(testId: string, e: React.MouseEvent) {
    e.stopPropagation();
    setRetryingId(testId);
    try {
      const { answers } = await getTestSession(examSlug, testId);
      const questionIds = answers.map((a: { question_id: number }) => a.question_id);
      if (questionIds.length === 0) return;
      const { session: newSession, questions } =
        await createTestMutation.mutateAsync({
          question_ids: questionIds,
          mode: "practice",
        });
      localStorage.setItem(
        `test_${newSession.id}`,
        JSON.stringify({ session: newSession, questions }),
      );
      navigate({
        to: "/exams/$slug/test/$id",
        params: { slug: examSlug, id: newSession.id },
      });
    } catch {
      setRetryingId(null);
    }
  }

  return (
    <div className="min-h-screen bg-zinc-50 dark:bg-zinc-950">
      <div className="mx-auto max-w-6xl px-4 py-6 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-zinc-900 dark:text-zinc-50 sm:text-3xl">
            {t("history.title")}
          </h1>
          <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">
            {t("history.desc")}
          </p>

          {/* Question Number Filter */}
          <div className="mt-4 flex items-end gap-2">
            <div className="flex-1 max-w-xs space-y-1">
              <label className="text-xs font-medium text-zinc-500">{t("history.filter_question_number")}</label>
              <div className="flex gap-2">
                <Input
                  type="number"
                  min={1}
                  placeholder={t("history.filter_question_number_placeholder")}
                  value={questionNumberInput}
                  onChange={(e) => setQuestionNumberInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      const num = Number(questionNumberInput);
                      setActiveQuestionNumber(num > 0 ? num : undefined);
                    }
                  }}
                  className="h-9"
                />
                <Button
                  variant="default"
                  size="sm"
                  className="h-9 shrink-0"
                  onClick={() => {
                    const num = Number(questionNumberInput);
                    setActiveQuestionNumber(num > 0 ? num : undefined);
                  }}
                >
                  <Search className="mr-1.5 h-4 w-4" />
                  {t("common.filter")}
                </Button>
              </div>
            </div>
            {activeQuestionNumber !== undefined && (
              <Button
                variant="ghost"
                size="sm"
                className="h-9 shrink-0 text-zinc-500"
                onClick={() => {
                  setQuestionNumberInput("");
                  setActiveQuestionNumber(undefined);
                }}
              >
                <X className="mr-1 h-3.5 w-3.5" />
                {t("common.reset")}
              </Button>
            )}
          </div>
          {activeQuestionNumber !== undefined && (
            <p className="mt-2 text-sm text-zinc-500">
              {locale === "en"
                ? `${t("history.filter_active")}${activeQuestionNumber}`
                : `${activeQuestionNumber}${t("history.filter_active")}`}
              {" · "}
              {tests.length}{t("common.count_suffix")}
            </p>
          )}
        </div>

        {/* Loading */}
        {loading && (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-zinc-400" />
            <span className="ml-2 text-zinc-500">{t("history.loading")}</span>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-center text-sm text-red-600 dark:border-red-800 dark:bg-red-950 dark:text-red-400">
            {t("common.error")}: {createErrorMessage(error)}
          </div>
        )}

        {/* Empty State */}
        {!loading && !error && tests.length === 0 && (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-16">
              <ClipboardList className="mb-4 h-12 w-12 text-zinc-300 dark:text-zinc-600" />
              <p className="text-lg font-medium text-zinc-500 dark:text-zinc-400">
                {t("history.no_history")}
              </p>
              <p className="mt-1 text-sm text-zinc-400 dark:text-zinc-500">
                {t("history.no_history_desc")}
              </p>
              <Button
                className="mt-4"
                onClick={() =>
                  navigate({
                    to: "/exams/$slug/test/new",
                    params: { slug: examSlug },
                  })
                }
              >
                {t("history.start_test")}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Test History List */}
        {!loading && !error && tests.length > 0 && (
          <>
            {/* Desktop Table */}
            <Card className="hidden sm:block">
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-zinc-200 dark:border-zinc-700">
                        <th className="px-4 py-3 text-left font-semibold text-zinc-600 dark:text-zinc-400">
                          {t("common.date")}
                        </th>
                        <th className="px-4 py-3 text-left font-semibold text-zinc-600 dark:text-zinc-400">
                          {t("history.mode")}
                        </th>
                        <th className="px-4 py-3 text-right font-semibold text-zinc-600 dark:text-zinc-400">
                          {t("history.question_count")}
                        </th>
                        <th className="px-4 py-3 text-right font-semibold text-zinc-600 dark:text-zinc-400">
                          {t("history.correct_count")}
                        </th>
                        <th className="px-4 py-3 text-right font-semibold text-zinc-600 dark:text-zinc-400">
                          {t("common.accuracy")}
                        </th>
                        <th className="px-4 py-3 text-center font-semibold text-zinc-600 dark:text-zinc-400">
                          {t("common.status")}
                        </th>
                        <th className="px-4 py-3 text-center font-semibold text-zinc-600 dark:text-zinc-400">
                          {t("history.retry")}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedTests.map((test) => {
                        const accuracy = getAccuracy(test);
                        const status = getStatusLabel(test);

                        return (
                          <tr
                            key={test.id}
                            className="cursor-pointer border-b border-zinc-100 transition-colors last:border-0 hover:bg-zinc-50 dark:border-zinc-800 dark:hover:bg-zinc-900"
                            onClick={() =>
                              test.completed_at
                                ? navigate({
                                    to: "/exams/$slug/test/$id/result",
                                    params: { slug: examSlug, id: test.id },
                                  })
                                : navigate({
                                    to: "/exams/$slug/test/$id",
                                    params: { slug: examSlug, id: test.id },
                                  })
                            }
                          >
                            <td className="px-4 py-3 text-zinc-700 dark:text-zinc-300">
                              <div className="flex items-center gap-2">
                                <Calendar className="h-4 w-4 text-zinc-400" />
                                {formatDate(test.started_at)}
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <Badge
                                variant={
                                  test.mode === "practice"
                                    ? "secondary"
                                    : "outline"
                                }
                                className="text-xs"
                              >
                                {test.mode === "practice"
                                  ? t("common.practice")
                                  : t("common.exam")}
                              </Badge>
                            </td>
                            <td className="px-4 py-3 text-right text-zinc-600 dark:text-zinc-400">
                              {test.total_questions}
                            </td>
                            <td className="px-4 py-3 text-right text-zinc-600 dark:text-zinc-400">
                              {test.correct_count}
                            </td>
                            <td className="px-4 py-3 text-right">
                              <Badge
                                variant={getScoreBadgeVariant(accuracy)}
                              >
                                {accuracy}%
                              </Badge>
                            </td>
                            <td className="px-4 py-3 text-center">
                              {!test.completed_at ? (
                                <Badge variant="outline" className="text-xs border-blue-300 text-blue-600 dark:border-blue-700 dark:text-blue-400">
                                  {t("history.continue")}
                                </Badge>
                              ) : (
                                <Badge variant={status.variant} className="text-xs">
                                  {status.text}
                                </Badge>
                              )}
                            </td>
                            <td className="px-4 py-3 text-center">
                              {test.completed_at && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-8 text-xs text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-950"
                                  onClick={(e) => handleRetake(test.id, e)}
                                  disabled={retryingId === test.id}
                                >
                                  {retryingId === test.id ? (
                                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                  ) : (
                                    <RotateCcw className="mr-1 h-3.5 w-3.5" />
                                  )}
                                  {retryingId !== test.id && t("history.retry")}
                                </Button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            {/* Mobile Card List */}
            <div className="space-y-3 sm:hidden">
              {paginatedTests.map((test) => {
                const accuracy = getAccuracy(test);
                const status = getStatusLabel(test);

                return (
                  <Card
                    key={test.id}
                    className="cursor-pointer transition-shadow hover:shadow-md"
                    onClick={() =>
                      test.completed_at
                        ? navigate({
                            to: "/exams/$slug/test/$id/result",
                            params: { slug: examSlug, id: test.id },
                          })
                        : navigate({
                            to: "/exams/$slug/test/$id",
                            params: { slug: examSlug, id: test.id },
                          })
                    }
                  >
                    <CardContent className="py-4">
                      <div className="flex items-start justify-between">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <Badge
                              variant={
                                test.mode === "practice"
                                  ? "secondary"
                                  : "outline"
                              }
                              className="text-[10px]"
                            >
                              {test.mode === "practice"
                                ? t("common.practice")
                                : t("common.exam")}
                            </Badge>
                            {!test.completed_at ? (
                              <Badge variant="outline" className="text-[10px] border-blue-300 text-blue-600 dark:border-blue-700 dark:text-blue-400">
                                {t("history.continue")}
                              </Badge>
                            ) : (
                              <Badge variant={status.variant} className="text-[10px]">
                                {status.text}
                              </Badge>
                            )}
                          </div>
                          <p className="mt-2 text-xs text-zinc-400">
                            {formatDate(test.started_at)}
                          </p>
                        </div>
                        <Badge
                          variant={getScoreBadgeVariant(accuracy)}
                          className="text-sm"
                        >
                          {accuracy}%
                        </Badge>
                      </div>
                      <div className="mt-3 flex items-center justify-between">
                        <div className="flex items-center gap-4 text-xs text-zinc-500 dark:text-zinc-400">
                          <span>{t("history.question_count")}: {test.total_questions}</span>
                          <span>{t("common.correct")}: {test.correct_count}</span>
                        </div>
                        {test.completed_at && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 text-xs text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-950"
                            onClick={(e) => handleRetake(test.id, e)}
                            disabled={retryingId === test.id}
                          >
                            {retryingId === test.id ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <RotateCcw className="mr-1 h-3.5 w-3.5" />
                            )}
                            {retryingId !== test.id && t("history.retry")}
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-6 flex items-center justify-center gap-4">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  <ChevronLeft className="mr-1 h-4 w-4" />
                  {t("common.previous")}
                </Button>
                <span className="text-sm text-zinc-600 dark:text-zinc-400">
                  {page} / {totalPages} {t("common.page")}
                  <span className="ml-2 text-zinc-400">
                    ({t("common.total")} {tests.length})
                  </span>
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                >
                  {t("common.next")}
                  <ChevronRight className="ml-1 h-4 w-4" />
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
